// @flow
import { of } from 'rxjs';
import { debounceTime, map, catchError, switchMap, takeUntil, mergeMap } from 'rxjs/operators';
import { endpoints, getActivePurchasesApi, getPastPurchasesApi, deleteOrCancelOrderApi } from './api';
import update from 'immutability-helper';
import Paginator from '../../common/Paginator';
import { setNewAlert } from '../alert/duck';
import { ofType, StateObservable } from 'redux-observable';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../error';
import type { Order, Transaction } from '../../entities';
import type { RootState } from '../index';

type OrdersAction = {
  +type: string;
  +fetchActivesError?: HTTPError;
  +activePurchases?: Order[];
  +fetchPastError?: HTTPError;
  +pastPurchases?: Transaction[];
  +queryFilter?: { [key: string]: string };
  +resetPagination?: boolean;
  +error?: HTTPError;
  +orderId?: string;
};

export type OrdersState = {
  +isFetchingActives?: boolean;
  +activePurchases?: Order[];
  +fetchActivesError?: HTTPError;
  +isFetchingPasts?: boolean;
  +pastPurchases?: Transaction[];
  +pastPurchasesPage?: number;
  +pastPurchasesPageCanAsk?: boolean;
  +fetchPastError?: HTTPError;
  +isDeletingOrCancelingOrder?: boolean;
  +deleteOrCancelSuccess?: boolean;
  +deleteOrCancelOrderError?: HTTPError;
};

// Actions
const ORDERS_RESET_STATE = 'ORDERS_RESET_STATE';

const ORDERS_FETCH_ACTIVE_REQUEST = 'ORDERS_FETCH_ACTIVE_REQUEST';
const ORDERS_FETCH_ACTIVE_SUCCESS = 'ORDERS_FETCH_ACTIVE_SUCCESS';
const ORDERS_FETCH_ACTIVE_ERROR = 'ORDERS_FETCH_ACTIVE_ERROR';

const ORDERS_FETCH_PAST_REQUEST = 'ORDERS_FETCH_PAST_REQUEST';
const ORDERS_FETCH_PAST_SUCCESS = 'ORDERS_FETCH_PAST_SUCCESS';
const ORDERS_FETCH_PAST_ERROR = 'ORDERS_FETCH_PAST_ERROR';

const ORDERS_DELETE_CANCEL_OR_DELETE_REQUEST = 'ORDERS_DELETE_CANCEL_OR_DELETE_REQUEST';
const ORDERS_DELETE_CANCEL_OR_DELETE_SUCCESS = 'ORDERS_DELETE_CANCEL_OR_DELETE_SUCCESS';
const ORDERS_DELETE_CANCEL_OR_DELETE_ERROR = 'ORDERS_DELETE_CANCEL_OR_DELETE_ERROR';

// Action Creators
export function fetchActivePurchases(resetPagination?: boolean): OrdersAction {
  return {
    type: ORDERS_FETCH_ACTIVE_REQUEST,
    resetPagination,
  };
}

export function fetchActivePurchasesSuccess(activePurchases: Order[]): OrdersAction {
  return {
    type: ORDERS_FETCH_ACTIVE_SUCCESS,
    activePurchases,
  };
}

export function fetchActivePurchasesError(fetchActivesError: HTTPError): OrdersAction {
  return {
    type: ORDERS_FETCH_ACTIVE_ERROR,
    fetchActivesError,
  }
}

export function fetchPastPurchases(queryFilter?: { [key: string]: string } = {},
                                   resetPagination?: boolean): OrdersAction {
  return {
    type: ORDERS_FETCH_PAST_REQUEST,
    queryFilter,
    resetPagination,
  };
}

export function fetchPastPurchasesSuccess(pastPurchases: Transaction[]): OrdersAction {
  return {
    type: ORDERS_FETCH_PAST_SUCCESS,
    pastPurchases,
  };
}

export function fetchPastPurchasesError(fetchPastError: HTTPError): OrdersAction {
  return {
    type: ORDERS_FETCH_PAST_ERROR,
    fetchPastError,
  }
}

export function deleteOrCancelOrder(orderId: string): OrdersAction {
  return {
    type: ORDERS_DELETE_CANCEL_OR_DELETE_REQUEST,
    orderId,
  }
}

export function deleteOrCancelOrderSuccess(): OrdersAction {
  return {
    type: ORDERS_DELETE_CANCEL_OR_DELETE_SUCCESS,
  }
}

export function deleteOrCancelOrderErrorFn(error: HTTPError): OrdersAction {
  return {
    type: ORDERS_DELETE_CANCEL_OR_DELETE_ERROR,
    error,
  }
}


export function resetOrdersState(): OrdersAction {
  return {
    type: ORDERS_RESET_STATE,
  }
}

export const actions = {
  ORDERS_RESET_STATE,
  ORDERS_FETCH_ACTIVE_REQUEST,
  ORDERS_FETCH_ACTIVE_SUCCESS,
  ORDERS_FETCH_ACTIVE_ERROR,
  ORDERS_FETCH_PAST_REQUEST,
  ORDERS_FETCH_PAST_SUCCESS,
  ORDERS_FETCH_PAST_ERROR,
  ORDERS_DELETE_CANCEL_OR_DELETE_REQUEST,
  ORDERS_DELETE_CANCEL_OR_DELETE_SUCCESS,
  ORDERS_DELETE_CANCEL_OR_DELETE_ERROR,
  fetchActivePurchases,
  fetchActivePurchasesSuccess,
  fetchActivePurchasesError,
  fetchPastPurchases,
  fetchPastPurchasesSuccess,
  fetchPastPurchasesError,
  resetOrdersState,
  deleteOrCancelOrder,
  deleteOrCancelOrderSuccess,
  deleteOrCancelOrderErrorFn,
};

export const initialState: OrdersState = {
  pastPurchasesPage: 0,
};

const paginator = new Paginator(['pastPurchases']);

// Reducer
export default function reducer(state: OrdersState = initialState,
                                action: OrdersAction): OrdersState {
  switch (action.type) {
    case ORDERS_FETCH_ACTIVE_REQUEST:
      return {
        isFetchingActives: true,
      };
    case ORDERS_FETCH_ACTIVE_SUCCESS:
      return {
        activePurchases: action.activePurchases,
      };
    case ORDERS_FETCH_ACTIVE_ERROR:
      return {
        fetchActivesError: action.fetchActivesError,
      };

    case ORDERS_FETCH_PAST_REQUEST:
      if (action.resetPagination) {
        paginator.reset('pastPurchases');
        return {
          isFetchingPasts: true,
          pastOrders: paginator.getItems('pastPurchases'),
          pastOrdersPageCanAsk: paginator.getCanAsk('pastPurchases'),
          pastOrdersPage: paginator.getPage('pastPurchases'),
        };
      }
      return {
        ...state,
        isFetchingPasts: true,
      };
    case ORDERS_FETCH_PAST_SUCCESS:
      paginator.increment('pastPurchases', action.pastPurchases, state.pastPurchases);
      return {
        pastPurchases: paginator.getItems('pastPurchases'),
        pastPurchasesPageCanAsk: paginator.getCanAsk('pastPurchases'),
        pastPurchasesPage: paginator.getPage('pastPurchases'),
      };
    case ORDERS_FETCH_PAST_ERROR:
      return {
        fetchPastError: action.fetchPastError,
        pastPurchasesPage: state.pastPurchasesPage,
      };

    case ORDERS_DELETE_CANCEL_OR_DELETE_REQUEST:
      return update(state, {
        isDeletingOrCancelingOrder: { $set: true },
        $unset: ['deleteOrCancelOrderError', 'deleteOrCancelSuccess'],
      });
    case ORDERS_DELETE_CANCEL_OR_DELETE_SUCCESS:
      return update(state, {
        deleteOrCancelSuccess: { $set: true },
        $unset: ['isDeletingOrCancelingOrder'],
      });
    case ORDERS_DELETE_CANCEL_OR_DELETE_ERROR:
      return update(state, {
        deleteOrCancelOrderError: { $set: action.error },
        $unset: ['isDeletingOrCancelingOrder'],
      });

    case ORDERS_RESET_STATE:
      paginator.resetAll();
      return initialState;
    default:
      return state;
  }
}

// Epics
export function fetchActivePurchasesEpic(action$: Observable<OrdersAction>): Observable<OrdersAction> {
  return action$.pipe(
    ofType(ORDERS_FETCH_ACTIVE_REQUEST),
    debounceTime(600),
    switchMap(() => getActivePurchasesApi().pipe(
      takeUntil(action$.pipe(ofType(ORDERS_RESET_STATE))),
      takeUntil(action$.pipe(ofType(ORDERS_FETCH_ACTIVE_REQUEST))),
      map((response: Order[]) => fetchActivePurchasesSuccess(response)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchActivePurchasesError(e),
            setNewAlert('orders', 'error', undefined, 'httpErrors.fetch.500'),
          );
        } else if (e.code === 400) {
          return of(fetchActivePurchasesError(e),
            setNewAlert('orders', 'error', undefined, 'httpErrors.fetch.400'),
          );
        }
        return of(fetchActivePurchasesError(e));
      }))
    ));
}

export function fetchPastPurchasesEpic(action$: Observable<OrdersAction>,
                                       state$: StateObservable<RootState>): Observable<OrdersAction> {
  return action$.pipe(
    ofType(ORDERS_FETCH_PAST_REQUEST),
    debounceTime(600),
    switchMap(({ queryFilter }) => getPastPurchasesApi({
        ...queryFilter,
        page: state$.value.orders.pastPurchasesPage || paginator.getPage('pastPurchases'),
        limit: paginator.getLimit(),
      }).pipe(
      takeUntil(action$.pipe(ofType(ORDERS_RESET_STATE))),
      takeUntil(action$.pipe(ofType(ORDERS_FETCH_PAST_REQUEST))),
      map((response: Order[]) => fetchPastPurchasesSuccess(response)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchPastPurchasesError(e),
            setNewAlert('orders', 'error', undefined, 'httpErrors.fetch.500'),
          );
        } else if (e.code === 400) {
          return of(fetchPastPurchasesError(e),
            setNewAlert('orders', 'error', undefined, 'httpErrors.fetch.400'),
          );
        }
        return of(fetchPastPurchasesError(e));
      }))
    ));
}

export function cancelOrDeleteOrderEpic(action$: Observable<OrdersAction>): Observable<OrdersAction> {
  return action$.pipe(
    ofType(ORDERS_DELETE_CANCEL_OR_DELETE_REQUEST),
    mergeMap(({ orderId }) => deleteOrCancelOrderApi(undefined, {
        route: endpoints.DELETE_OR_CANCEL.replace(':id', orderId)
      }).pipe(
      mergeMap(() => of(deleteOrCancelOrderSuccess(), fetchActivePurchases())),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(deleteOrCancelOrderErrorFn(e),
            setNewAlert('orders', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(deleteOrCancelOrderErrorFn(e),
            setNewAlert('orders', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(deleteOrCancelOrderErrorFn(e));
      }))
    ));
}



// WEBPACK FOOTER //
// ./src/modules/orders/duck.js